import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ShieldCheck, User, Clock, MagnifyingGlass,
  SignIn, UserPlus, Pencil, Trash, Gear, Broadcast, Key
} from '@phosphor-icons/react';

const ACTION_ICONS = {
  login: SignIn, login_failed: ShieldCheck, register: UserPlus,
  create: Pencil, update: Gear, delete: Trash,
  settings_change: Gear, '2fa_enable': Key, '2fa_disable': Key, broadcast: Broadcast,
};

const ACTION_COLORS = {
  login: '#22C55E', login_failed: '#EF4444', register: '#3B82F6',
  create: '#FF6B35', update: '#F59E0B', delete: '#EF4444',
  settings_change: '#8B5CF6', '2fa_enable': '#00E5FF', '2fa_disable': '#F59E0B', broadcast: '#FF6B35',
};

export default function AuditLogPage() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ action: '', resource: '' });
  const [page, setPage] = useState(0);
  const limit = 30;

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: limit.toString(), offset: (page * limit).toString() });
        if (filter.action) params.set('action', filter.action);
        if (filter.resource) params.set('resource', filter.resource);
        const res = await api.get(`/audit?${params}`);
        setLogs(res.data.logs);
        setTotal(res.data.total);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, [api, page, filter]);

  return (
    <div data-testid="audit-log-page" className="space-y-6 max-w-5xl">
      <div>
        <p className="overline">Security</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Audit Log</h1>
        <p className="text-sm text-neutral-500 mt-1">Track all admin actions and security events</p>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap" data-testid="audit-filters">
        <select value={filter.action} onChange={e => { setFilter(f => ({ ...f, action: e.target.value })); setPage(0); }}
          data-testid="filter-action"
          className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
          <option value="">All Actions</option>
          {['login', 'login_failed', 'register', 'create', 'update', 'delete', 'settings_change', '2fa_enable', '2fa_disable', 'broadcast'].map(a => (
            <option key={a} value={a}>{a.replace('_', ' ')}</option>
          ))}
        </select>
        <select value={filter.resource} onChange={e => { setFilter(f => ({ ...f, resource: e.target.value })); setPage(0); }}
          data-testid="filter-resource"
          className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
          <option value="">All Resources</option>
          {['auth', 'staff', 'expense', 'announcement', 'settings', 'calendar', 'member', 'document', 'vehicle', 'maintenance'].map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <span className="text-xs text-neutral-500 self-center ml-auto">{total} total entries</span>
      </div>

      {/* Log List */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : logs.length === 0 ? (
        <div className="glass-card p-10 text-center">
          <MagnifyingGlass size={40} weight="duotone" className="text-neutral-600 mx-auto mb-3" />
          <p className="text-neutral-500 text-sm">No audit logs found. Actions will be recorded here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log, i) => {
            const Icon = ACTION_ICONS[log.action] || ShieldCheck;
            const color = ACTION_COLORS[log.action] || '#6B7280';
            return (
              <div key={i} data-testid={`audit-entry-${i}`}
                className="glass-card p-4 flex items-center gap-4 animate-fade-up"
                style={{ animationDelay: `${i * 20}ms` }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${color}15` }}>
                  <Icon size={16} style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-white">{log.user_name || log.user_id || 'System'}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                      style={{ backgroundColor: `${color}15`, color }}>{log.action?.replace('_', ' ')}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-neutral-400 font-medium">{log.resource}</span>
                  </div>
                  {log.details && <p className="text-[11px] text-neutral-500 mt-0.5 truncate">{log.details}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[11px] text-neutral-500">
                    {new Date(log.timestamp).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {log.ip_address && <p className="text-[10px] text-neutral-600 font-mono">{log.ip_address}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="px-4 py-2 rounded-lg border border-white/10 text-sm text-neutral-400 disabled:opacity-30 hover:text-white transition-colors">
            Previous
          </button>
          <span className="text-xs text-neutral-500">Page {page + 1} of {Math.ceil(total / limit)}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}
            className="px-4 py-2 rounded-lg border border-white/10 text-sm text-neutral-400 disabled:opacity-30 hover:text-white transition-colors">
            Next
          </button>
        </div>
      )}
    </div>
  );
}
